import React, { useRef } from "react";
import {
  Container,
  Col,
  Row,
  Card,
  CardBody,
  Button,
  Breadcrumb,
  BreadcrumbItem,
  CardHeader,
  Label,
  CardFooter,
  FormFeedback,
  Spinner,
} from "reactstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Form, Formik, Field } from "formik";
import { initialValues, schema } from "./constants";
import { useState } from "react";
import { FormSelection } from "@workspace/common";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { fetchCountryCurrency } from "@workspace/common/src/store/actions";
import {
  createBenefit,
  getBenefit,
  resetBenefit,
  updateBenefit,
} from "../../store/benefits/action";

const AddBenefits = () => {
  document.title = "Save Benefit | RTS";

  /**
   * Get id from url
   */
  const { id } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const form = useRef();

  const allCountries = useSelector(
    (state) => state?.CountryCurrencyReducer?.countryCurrency
  );
  const benefit = useSelector((state) => state.BenefitsReducer.benefit);
  const benefitCreateMeta = useSelector(
    (state) => state.BenefitsReducer.benefitCreateMeta
  );
  const benefitUpdateMeta = useSelector(
    (state) => state.BenefitsReducer.benefitUpdateMeta
  );

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [sortByCountry, setSortByCountry] = useState(null);

  const handleSubmit = async (values) => {
    const benefit = {
      country: values.country.trim(),
      type: values.benefitType.trim(),
      value: values.benefitValue.trim(),
    };
    if (id) {
      benefit.id = id;
      dispatch(updateBenefit({ benefit, navigate: navigate }));
    } else {
      dispatch(createBenefit({ benefit, navigate: navigate }));
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(getBenefit(id));
    }
    return () => {
      dispatch(resetBenefit());
    };
  }, []);

  useEffect(() => {
    if (benefit) {
      const values = {
        country: benefit.country,
        benefitType: benefit.type,
        benefitValue: benefit.value,
      };
      if (form.current) {
        setSortByCountry({ label: benefit.country, value: benefit.country });
        form.current.setValues(values);
      }
    }
  }, [benefit]);

  useEffect(() => {
    if (allCountries && allCountries?.length > 0) {
      setSelectedCountry([
        {
          options: allCountries?.map((country) => ({
            label: country.name,
            value: country.name,
          })),
        },
      ]);
    }
  }, [allCountries]);

  useEffect(() => {
    dispatch(fetchCountryCurrency());
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col>
              <Breadcrumb>
                <BreadcrumbItem>
                  <Link to="/settings/general">Settings</Link>
                </BreadcrumbItem>
                <BreadcrumbItem>
                  <Link to="/settings/benefits">Benefits</Link>
                </BreadcrumbItem>
                <BreadcrumbItem active>Save Benefit</BreadcrumbItem>
              </Breadcrumb>
            </Col>
          </Row>
          <Row>
            <Col>
              <Card>
                <CardHeader className="bg-header">
                  <div className="d-flex flex-column">
                    <span className="fs-5 fw-bold">Save Benefits</span>
                  </div>
                </CardHeader>
                <Formik
                  innerRef={form}
                  initialValues={initialValues}
                  validateOnBlur
                  validationSchema={schema}
                  onSubmit={handleSubmit}
                >
                  {({ errors, touched, handleChange }) => (
                    <Form>
                      <CardBody>
                        <Row>
                          <Col lg={4}>
                            <div className="mb-3">
                              <FormSelection
                                label="Country*"
                                type="text"
                                name="country"
                                options={selectedCountry}
                                value={sortByCountry}
                                onChange={(selectedCountry) => {
                                  setSortByCountry(selectedCountry);
                                  if (!selectedCountry) {
                                    handleChange("country")("");
                                  } else {
                                    handleChange("country")(
                                      selectedCountry?.value
                                    );
                                  }
                                }}
                                isClearable
                                error={touched.country && errors.country}
                              />
                            </div>
                          </Col>
                          <Col lg={4}>
                            <div className="mb-3">
                              <Label>Benefit type*</Label>
                              <Field
                                name="benefitType"
                                as="select"
                                className={`form-select ${
                                  errors.benefitType && touched.benefitType
                                    ? "is-invalid"
                                    : ""
                                }`}
                              >
                                <option value="">Select</option>
                                <option value="Monetory">Monetory</option>
                                <option value="Non-Monetory">
                                  Non-Monetory
                                </option>
                              </Field>
                              {errors.benefitType && touched.benefitType && (
                                <FormFeedback typeof="invalid">
                                  {errors.benefitType}
                                </FormFeedback>
                              )}
                            </div>
                          </Col>
                          <Col lg={4}>
                            <div className="mb-3">
                              <Label>Benefit value*</Label>
                              <Field
                                name="benefitValue"
                                type="text"
                                placeholder="Enter a benifit value"
                                className={`form-control ${
                                  touched.benefitValue && errors.benefitValue
                                    ? "is-invalid"
                                    : ""
                                }`}
                              />
                              {errors.benefitValue && touched.benefitValue && (
                                <FormFeedback typeof="invalid">
                                  {errors.benefitValue}
                                </FormFeedback>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                      <CardFooter>
                        <Row>
                          <Col>
                            <div className="d-flex  justify-content-end">
                              <Button
                                className="btn btn-custom-primary"
                                type="submit"
                                disabled={false}
                              >
                                Save{" "}
                                {(benefitCreateMeta?.isLoading ||
                                  benefitUpdateMeta?.isLoading) && (
                                  <Spinner size="sm"></Spinner>
                                )}
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </CardFooter>
                    </Form>
                  )}
                </Formik>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AddBenefits;
